import React, { useState } from 'react';
import {
  X, Download, Upload, CheckCircle, XCircle, Send,
  History, Eye, Calendar, User, FileText, Clock,
  FileImage, Loader2, AlertCircle,
} from 'lucide-react';
import useEmployeeDocuments from '../../../hooks/useEmployeeDocuments';
import {
  DocumentStatusBadge,
  ConfidentialityBadge,
  CategoryIcon,
  ExpirationAlert,
  DocumentPreview,
} from './components';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' && d.length === 10 ? d + 'T12:00:00' : d;
  return new Date(s).toLocaleDateString('es-DO', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-DO', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024)          return bytes + ' B';
  if (bytes < 1024 * 1024)   return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function DocumentDetails({ document, onClose, onUpdate }) {
  const {
    approveDocument, rejectDocument, submitDocument,
    downloadDocument, uploadNewVersion, loadVersions, loadDownloadLog, loading,
  } = useEmployeeDocuments();

  const [panel,           setPanel]           = useState(null); // 'reject' | 'newVersion' | 'versions' | 'downloads' | null
  const [rejectReason,    setRejectReason]    = useState('');
  const [versions,        setVersions]        = useState([]);
  const [downloads,       setDownloads]       = useState([]);
  const [newVersionFile,  setNewVersionFile]  = useState(null);
  const [versionReason,   setVersionReason]   = useState('');
  const [showPreview,     setShowPreview]     = useState(false);
  const [actionError,     setActionError]     = useState(null);

  // ── Acciones ─────────────────────────────────────────────────

  const doAction = async (fn, successCb) => {
    setActionError(null);
    const result = await fn();
    if (result.success) {
      onUpdate();
      onClose();
      successCb?.();
    } else {
      setActionError(result.error || 'Ocurrió un error');
    }
  };

  const handleSubmit   = () => doAction(() => submitDocument(document.id));
  const handleApprove  = () => doAction(() => approveDocument(document.id));
  const handleDownload = () => downloadDocument(document.id, document.archivo_nombre);

  const handleReject = async () => {
    if (!rejectReason.trim()) { setActionError('Debe proporcionar un motivo'); return; }
    doAction(() => rejectDocument(document.id, rejectReason));
  };

  const handleUploadVersion = async () => {
    if (!newVersionFile)           { setActionError('Seleccione un archivo'); return; }
    if (!versionReason.trim())     { setActionError('Ingrese un motivo del cambio'); return; }
    doAction(() => uploadNewVersion(document.id, newVersionFile, versionReason));
  };

  const openVersions = async () => {
    const result = await loadVersions(document.id);
    if (result.success) { setVersions(result.versions || []); setPanel('versions'); }
  };

  const openDownloads = async () => {
    const result = await loadDownloadLog(document.id);
    if (result.success) { setDownloads(result.downloads || []); setPanel('downloads'); }
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start gap-3">
            <CategoryIcon categoria={document.categoria_codigo} size="md" />
            <div>
              <h2 className="text-base font-bold text-gray-900 leading-tight">
                {document.documento_nombre || document.nombre}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">{document.tipo_documento_nombre}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <DocumentStatusBadge estado={document.estado} size="sm" />
                <ConfidentialityBadge nivel={document.nivel_confidencialidad} />
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Error */}
          {actionError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={14} className="flex-shrink-0" /> {actionError}
            </div>
          )}

          {/* Info del empleado */}
          <Section title="Empleado" icon={<User size={14} />}>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="Nombre" value={document.empleado_nombre} />
              <InfoRow label="Código" value={document.empleado_codigo} />
            </div>
          </Section>

          {/* Info del documento */}
          <Section title="Documento" icon={<FileText size={14} />}>
            <div className="space-y-2 text-sm">
              {document.descripcion && <InfoRow label="Descripción" value={document.descripcion} />}
              {document.numero_documento && <InfoRow label="Número" value={document.numero_documento} mono />}
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Archivo"  value={document.archivo_nombre} />
                <InfoRow label="Tamaño"   value={fmtSize(document.archivo_tamano)} />
                <InfoRow label="Versión"  value={`v${document.version_actual}`} />
                <InfoRow label="Descargas" value={document.total_descargas || 0} />
              </div>
            </div>
          </Section>

          {/* Fechas */}
          <Section title="Fechas" icon={<Calendar size={14} />}>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {document.fecha_emision    && <InfoRow label="Emisión"     value={fmtDate(document.fecha_emision)} />}
              {document.fecha_vencimiento && <InfoRow label="Vencimiento" value={fmtDate(document.fecha_vencimiento)} />}
              <InfoRow label="Creado" value={fmtDateTime(document.created_at)} sub={document.creado_por ? `por ${document.creado_por}` : null} />
            </div>
            {document.dias_restantes !== null && (
              <div className="mt-3">
                <ExpirationAlert fechaVencimiento={document.fecha_vencimiento} diasRestantes={document.dias_restantes} />
              </div>
            )}
          </Section>

          {/* Workflow aprobación */}
          {(document.submitted_at || document.approved_at || document.rejected_at) && (
            <Section title="Historial de Aprobación" icon={<Clock size={14} />}>
              <div className="space-y-3 text-sm">
                {document.submitted_at && (
                  <WorkflowRow icon={<Send size={13} className="text-blue-500" />} label="Enviado a aprobación"
                    date={fmtDateTime(document.submitted_at)} by={document.solicitado_por} />
                )}
                {document.approved_at && (
                  <WorkflowRow icon={<CheckCircle size={13} className="text-green-500" />} label="Aprobado"
                    date={fmtDateTime(document.approved_at)} by={document.aprobado_por} />
                )}
                {document.rejected_at && (
                  <WorkflowRow icon={<XCircle size={13} className="text-red-500" />} label="Rechazado"
                    date={fmtDateTime(document.rejected_at)} by={document.rechazado_por}>
                    {document.motivo_rechazo && (
                      <p className="mt-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                        {document.motivo_rechazo}
                      </p>
                    )}
                  </WorkflowRow>
                )}
              </div>
            </Section>
          )}

          {/* Botones historial */}
          <div className="flex gap-3">
            <button onClick={openVersions} className="flex-1 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5 transition-colors">
              <History size={13} /> Versiones ({document.total_versiones || 0})
            </button>
            <button onClick={openDownloads} className="flex-1 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5 transition-colors">
              <Eye size={13} /> Descargas ({document.total_descargas || 0})
            </button>
          </div>

          {/* Panel: Rechazo */}
          {panel === 'reject' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-3">
              <p className="text-sm font-semibold text-red-900">Motivo del rechazo</p>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Describe el motivo..."
                rows={3}
                className="w-full text-sm px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:border-red-500 bg-white"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">Cancelar</button>
                <button onClick={handleReject} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                  {loading && <Loader2 size={11} className="animate-spin" />} Confirmar Rechazo
                </button>
              </div>
            </div>
          )}

          {/* Panel: Nueva versión */}
          {panel === 'newVersion' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
              <p className="text-sm font-semibold text-blue-900">Subir Nueva Versión</p>
              <input type="file" onChange={e => setNewVersionFile(e.target.files[0])} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="text-sm w-full" />
              <textarea
                value={versionReason}
                onChange={e => setVersionReason(e.target.value)}
                placeholder="Describe los cambios..."
                rows={2}
                className="w-full text-sm px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">Cancelar</button>
                <button onClick={handleUploadVersion} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  {loading && <Loader2 size={11} className="animate-spin" />} Subir Versión
                </button>
              </div>
            </div>
          )}

          {/* Panel: Versiones */}
          {panel === 'versions' && (
            <ListPanel title="Historial de Versiones" onClose={() => setPanel(null)}>
              {versions.length === 0
                ? <EmptyPanelMsg text="No hay versiones anteriores" />
                : versions.map(v => (
                  <div key={v.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex justify-between">
                      <p className="text-sm font-semibold text-gray-900">Versión {v.version_numero}</p>
                      <span className="text-xs text-gray-400">{fmtSize(v.archivo_tamano)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{v.archivo_nombre}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fmtDateTime(v.created_at)}{v.creado_por && ` · ${v.creado_por}`}</p>
                    {v.motivo_cambio && <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1">{v.motivo_cambio}</p>}
                  </div>
                ))
              }
            </ListPanel>
          )}

          {/* Panel: Descargas */}
          {panel === 'downloads' && (
            <ListPanel title="Registro de Descargas" onClose={() => setPanel(null)}>
              {downloads.length === 0
                ? <EmptyPanelMsg text="No hay descargas registradas" />
                : downloads.map(d => (
                  <div key={d.id} className="p-3 border border-gray-200 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900">{d.descargado_por || 'Usuario desconocido'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fmtDateTime(d.downloaded_at)}{d.version_numero && ` · v${d.version_numero}`}</p>
                    {d.ip_address && <p className="text-xs text-gray-400">IP: {d.ip_address}</p>}
                  </div>
                ))
              }
            </ListPanel>
          )}

        </div>

        {/* Footer acciones */}
        <div className="flex items-center gap-2 flex-wrap px-5 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {/* Preview */}
          {document.archivo_url && (
            <button onClick={() => setShowPreview(true)} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors">
              <FileImage size={13} /> Previsualizar
            </button>
          )}

          {/* Descargar */}
          {document.archivo_url && (
            <button onClick={handleDownload} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Download size={13} /> Descargar
            </button>
          )}

          {/* Enviar a aprobación */}
          {document.estado === 'draft' && (
            <button onClick={handleSubmit} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar a Aprobación
            </button>
          )}

          {/* Aprobar */}
          {document.estado === 'pending' && (
            <button onClick={handleApprove} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Aprobar
            </button>
          )}

          {/* Rechazar */}
          {document.estado === 'pending' && (
            <button onClick={() => setPanel('reject')} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
              <XCircle size={13} /> Rechazar
            </button>
          )}

          {/* Nueva versión */}
          {document.estado === 'approved' && (
            <button onClick={() => setPanel('newVersion')} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors">
              <Upload size={13} /> Nueva Versión
            </button>
          )}

          <button onClick={onClose} className="ml-auto px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
            Cerrar
          </button>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <DocumentPreview document={document} onClose={() => setShowPreview(false)} onDownload={handleDownload} />
      )}
    </div>
  );
}

// ── Sub-componentes ───────────────────────────────────────────

function Section({ title, icon, children }) {
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-3">
        {icon}{title}
      </h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono, sub }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`text-sm font-medium text-gray-900 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function WorkflowRow({ icon, label, date, by, children }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5">{icon}</div>
      <div>
        <p className="font-semibold text-gray-900">{label}</p>
        <p className="text-xs text-gray-400">{date}{by && ` · ${by}`}</p>
        {children}
      </div>
    </div>
  );
}

function ListPanel({ title, onClose, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
          <X size={15} />
        </button>
      </div>
      <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function EmptyPanelMsg({ text }) {
  return <p className="text-xs text-gray-400 text-center py-6">{text}</p>;
}
